import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { analyticsService } from "./utils/api";

// Layouts
import Header from "./components/Header";
import Footer from "./components/Footer";
import MobileBottomNav from "./components/MobileBottomNav";
import CookieConsent, { getCookieConsent } from "./components/CookieConsent";

// Public Pages (Lazy Loaded — one chunk per route instead of one giant bundle)
const Home = React.lazy(() => import("./pages/Home"));
const NewsListPage = React.lazy(() => import("./pages/NewsListPage"));
const NewsDetailPage = React.lazy(() => import("./pages/NewsDetailPage"));
const AnnouncementsPage = React.lazy(() => import("./pages/AnnouncementsPage"));
const EventsPage = React.lazy(() => import("./pages/EventsPage"));
const EventDetailPage = React.lazy(() => import("./pages/EventDetailPage"));
const TendersPage = React.lazy(() => import("./pages/TendersPage"));
const ProjectsPage = React.lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = React.lazy(() => import("./pages/ProjectDetailPage"));
const PhotoGalleryPage = React.lazy(() => import("./pages/PhotoGalleryPage"));
const PhotoGalleryDetailPage = React.lazy(() => import("./pages/PhotoGalleryDetailPage"));
const VideoGalleryPage = React.lazy(() => import("./pages/VideoGalleryPage"));
const MayorPage = React.lazy(() => import("./pages/MayorPage"));
const InstitutionalPage = React.lazy(() => import("./pages/InstitutionalPage"));
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const DepartmentsPage = React.lazy(() => import("./pages/DepartmentsPage"));
const CityGuidePage = React.lazy(() => import("./pages/CityGuidePage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const ServiceGuidePage = React.lazy(() => import("./pages/ServiceGuidePage"));
const ServiceGuideDetailPage = React.lazy(() => import("./pages/ServiceGuideDetailPage"));
const ServiceItemDetailPage = React.lazy(() => import("./pages/ServiceItemDetailPage"));

// 2007/4 sayılı genelge uyumluluğu için eklenen sayfalar
const LegislationPage = React.lazy(() => import("./pages/LegislationPage"));
const ActivityReportsPage = React.lazy(() => import("./pages/ActivityReportsPage"));
const CouncilMembersPage = React.lazy(() => import("./pages/CouncilMembersPage"));
const InformationRequestPage = React.lazy(() => import("./pages/InformationRequestPage"));
const ComplaintPage = React.lazy(() => import("./pages/ComplaintPage"));
const FAQPage = React.lazy(() => import("./pages/FAQPage"));
const HelpPage = React.lazy(() => import("./pages/HelpPage"));
const StatisticsPage = React.lazy(() => import("./pages/StatisticsPage"));
const SearchPage = React.lazy(() => import("./pages/SearchPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = React.lazy(() => import("./pages/TermsOfServicePage"));

// Admin app (auth + all admin routes live in their own chunk, loaded only
// when someone actually visits /admin/*)
const AdminApp = React.lazy(() => import("./pages/admin/AdminApp"));

// Loading Fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Public Layout — wraps every public route via a nested route so Header/Footer
// stay mounted across navigations instead of being re-declared 21 times.
const PublicLayout = () => {
  const location = useLocation();
  const [consent, setConsent] = useState(getCookieConsent());

  useEffect(() => {
    if (consent === 'accepted') {
      analyticsService.trackPageview(location.pathname);
    }
  }, [location.pathname, consent]);

  return (
    <>
      <Header />
      <Suspense fallback={<LoadingSpinner />}>
        <Outlet />
      </Suspense>
      <Footer />
      <MobileBottomNav />
      <CookieConsent onDecision={setConsent} />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/haberler" element={<NewsListPage />} />
          <Route path="/haber/:id" element={<NewsDetailPage />} />
          <Route path="/duyurular" element={<AnnouncementsPage />} />
          <Route path="/etkinlikler" element={<EventsPage />} />
          <Route path="/etkinlik/:id" element={<EventDetailPage />} />
          <Route path="/ihaleler" element={<TendersPage />} />
          <Route path="/projeler" element={<ProjectsPage />} />
          <Route path="/proje/:id" element={<ProjectDetailPage />} />
          <Route path="/foto-galeri" element={<PhotoGalleryPage />} />
          <Route path="/foto-galeri/:id" element={<PhotoGalleryDetailPage />} />
          <Route path="/video-galeri" element={<VideoGalleryPage />} />
          <Route path="/baskan" element={<MayorPage />} />
          <Route path="/kurumsal" element={<InstitutionalPage />} />
          <Route path="/hizmetler" element={<ServicesPage />} />
          <Route path="/birimler" element={<DepartmentsPage />} />
          <Route path="/kent-rehberi" element={<CityGuidePage />} />
          <Route path="/hizmet-rehberi" element={<ServiceGuidePage />} />
          <Route path="/hizmet-rehberi/:slug" element={<ServiceGuideDetailPage />} />
          <Route path="/hizmet-rehberi/:categorySlug/:itemSlug" element={<ServiceItemDetailPage />} />
          <Route path="/iletisim" element={<ContactPage />} />
          <Route path="/mevzuat" element={<LegislationPage />} />
          <Route path="/faaliyet-raporlari" element={<ActivityReportsPage />} />
          <Route path="/meclis-uyeleri" element={<CouncilMembersPage />} />
          <Route path="/bilgi-edinme" element={<InformationRequestPage />} />
          <Route path="/sikayet-ve-geri-bildirim" element={<ComplaintPage />} />
          <Route path="/sss" element={<FAQPage />} />
          <Route path="/yardim" element={<HelpPage />} />
          <Route path="/istatistikler" element={<StatisticsPage />} />
          <Route path="/ara" element={<SearchPage />} />
          <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
          <Route path="/kullanim-kosullari" element={<TermsOfServicePage />} />
        </Route>

        {/* Admin App — own chunk, own Suspense, own AuthProvider */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminApp />
            </Suspense>
          }
        />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;
